import React, { useState } from "react"
import PropTypes from "prop-types"
import moment from "moment"
import { useTranslation } from "react-i18next"
import { Button } from "antd"
import { Icon } from "ui/Icon"
import { getEventColor } from "pagesV2/pro/Calendar/utils"
import {
  getDefaultCalendarColor,
  getPaymentAccentColor,
  getPrimaryCalendarTextColor,
  resolveBookingColors
} from "pagesV2/pro/Calendar/calendarColorUtils"
import { useTheme } from "context/AntdThemeContext"
import BookingPopover from "pagesV2/pro/Calendar/shared/BookingPopover"
import { DATE_FORMAT } from "modules/datepicker/constants"
import styles from "./index.module.css"

/** Figma shows three rows before the "+N more" link. */
const VISIBLE_ROWS = 3

/**
 * A trip covering several days would otherwise be drawn at its start time on every day it
 * touches, which says nothing about where in the trip today falls. This strip lifts those
 * bookings above the hour grid and gives each one its place in the run — "day 2/4" — with the
 * dates it spans.
 *
 * @param {Object[]} bookings the day's multi-day bookings, already normalised by the day grid
 */
const MultiDayStrip = ({ dayKey, bookings = [], getBookingConflict, setIsBookingModalOpen, setSelectedBookingId }) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  if (!bookings.length) return null

  const visible = isExpanded ? bookings : bookings.slice(0, VISIBLE_ROWS)
  const hiddenCount = bookings.length - visible.length

  const renderRow = (booking) => {
    const dates = Array.isArray(booking.booked_dates) ? booking.booked_dates : []
    const dayIndex = dates.indexOf(dayKey)
    const isFirstDay = dayIndex === 0
    const isLastDay = dayIndex === dates.length - 1
    const firstDate = dates[0] ? moment(dates[0], DATE_FORMAT) : null
    const lastDate = dates[dates.length - 1] ? moment(dates[dates.length - 1], DATE_FORMAT) : null

    const primaryTextColor = getPrimaryCalendarTextColor(theme)
    const previewColors = resolveBookingColors(booking.color, getDefaultCalendarColor(), primaryTextColor, getEventColor)
    const accentColor = getPaymentAccentColor(booking.payment_status) || previewColors.borderColor
    const isAffected = Boolean(getBookingConflict?.(booking))

    const guestCount = Number(booking.total_guest_count) || 0
    const capacity = Number(booking.max_guests) || 0

    return (
      <BookingPopover key={booking.id} booking={booking} colors={previewColors}>
        <button
          type="button"
          className={`${styles.row} ${isAffected ? styles.rowAffected : ""}`}
          style={{ backgroundColor: previewColors.backgroundColor }}
          onClick={() => {
            setIsBookingModalOpen(true)
            setSelectedBookingId(booking.id)
          }}
        >
          <span className={styles.accent} style={{ backgroundColor: accentColor }} aria-hidden />
          {/* The chevrons say the trip runs on past the edges of today. */}
          <span className={styles.edge}>
            {!isFirstDay && <Icon name="ChevronLeft" width={14} height={14} color="var(--color-text-secondary-dark)" />}
          </span>
          {isAffected && <Icon name="AlertTriangle" width={14} height={14} color="var(--color-error)" />}
          <span className={styles.title}>{booking.title}</span>
          {isFirstDay && booking.startLabel && <span className={styles.meta}>{booking.startLabel}</span>}
          {dayIndex >= 0 && dates.length > 1 && (
            <span className={styles.meta}>
              {t("calendar.multi_day.day_of", {
                defaultValue: "day {{index}}/{{total}}",
                index: dayIndex + 1,
                total: dates.length
              })}
            </span>
          )}
          {firstDate?.isValid() && lastDate?.isValid() && (
            <span className={`${styles.meta} ${styles.range}`}>
              {`· ${firstDate.format("MMM D")} - ${lastDate.format("MMM D, YYYY")}`}
            </span>
          )}
          {Boolean(guestCount && capacity) && (
            <span className={styles.guests}>
              {t("calendar.multi_day.booked", { defaultValue: "{{label}} booked", label: `${guestCount}/${capacity}` })}
            </span>
          )}
          <span className={styles.edge}>
            {!isLastDay && <Icon name="ChevronRight" width={14} height={14} color="var(--color-text-secondary-dark)" />}
          </span>
        </button>
      </BookingPopover>
    )
  }

  return (
    <div className={styles.strip}>
      <div className={styles.gutter}>
        <button
          type="button"
          className={styles.collapseButton}
          aria-expanded={!isCollapsed}
          aria-label={t(isCollapsed ? "calendar.multi_day.show" : "calendar.multi_day.hide", {
            defaultValue: isCollapsed ? "Show multi-day bookings" : "Hide multi-day bookings"
          })}
          onClick={() => setIsCollapsed((previous) => !previous)}
        >
          <Icon
            name={isCollapsed ? "ChevronRight" : "ChevronDown"}
            width={16}
            height={16}
            color="var(--color-text-secondary-dark)"
          />
        </button>
      </div>
      {!isCollapsed && (
        <div className={styles.rows}>
          {visible.map(renderRow)}
          {hiddenCount > 0 && (
            <Button type="link" size="small" className={styles.moreButton} onClick={() => setIsExpanded(true)}>
              {t("calendar.multi_day.more", { defaultValue: "+{{count}} more", count: hiddenCount })}
            </Button>
          )}
          {isExpanded && bookings.length > VISIBLE_ROWS && (
            <Button type="link" size="small" className={styles.moreButton} onClick={() => setIsExpanded(false)}>
              {t("calendar.blackout.collapse", { defaultValue: "Collapse" })}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

MultiDayStrip.propTypes = {
  /** The day on screen, as "YYYY-MM-DD" — its place in each run is read off this. */
  dayKey: PropTypes.string.isRequired,
  bookings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      startLabel: PropTypes.string,
      color: PropTypes.string,
      payment_status: PropTypes.string,
      booked_dates: PropTypes.array,
      total_guest_count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      max_guests: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  /** Returns the blackout a booking clashes with, or null. Omitted when the day is fully
   *  closed, where outlining every row would say nothing (see `marksBookingConflicts`). */
  getBookingConflict: PropTypes.func,
  setIsBookingModalOpen: PropTypes.func.isRequired,
  setSelectedBookingId: PropTypes.func.isRequired
}

export default MultiDayStrip
